import type { ReactNode } from 'react';
import type { ResolvedAxisChartSettings } from '../hooks/useGeneratedAxisChart.types';
import type { GeneratedAxisTooltipContext } from '../GeneratedAxisTooltip.types';
import type { LinesProps } from '../../AxisAndGrid/AxisAndGrid.types';
import type { ChartCheckHandler } from '../../../DataViz.types';
import { toNumber, toNumberTimestamp } from '../../../util.js';
import { convertToZonedTimestamp } from '../../../js/helpers/date-helpers/dateHelpers';
import { getKeyItem } from '../../../js/default-handlers/check-if-selected/checkIfSelected';

type Row = Record<string, unknown>;

export type TooltipEntry = { id: string; name: ReactNode; color?: string; text: ReactNode; unit?: string };

const COMPACT = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

const X_INDEX = new WeakMap<Row[], Map<string, Map<string, Row[]>>>();

const toX = (value: unknown): string => String(toNumberTimestamp(value) ?? value);

const getRowsAtX = (data: Row[], x_key: string, x: unknown): Row[] => {
  let by_key = X_INDEX.get(data);
  if (!by_key) X_INDEX.set(data, by_key = new Map());
  let index = by_key.get(x_key);
  if (!index) {
    index = new Map();
    for (const item of data) {
      const id = toX(item[x_key]);
      const rows = index.get(id);
      if (rows) rows.push(item);
      else index.set(id, [item]);
    }
    by_key.set(x_key, index);
  }
  return index.get(toX(x)) ?? [];
};

export const tooltipEntries = (row: Row, data: Row[], axes: ResolvedAxisChartSettings['axes'], props: GeneratedAxisTooltipContext = { axes }): TooltipEntry[] => {
  const x_key = (axes.bottom ?? axes.top)?.value_key;
  const key_axes = (['left', 'right'] as const).flatMap((side) => {
    const axis = axes[side];
    if (!axis) return [];
    return (axis.value_keys?.length ? axis.value_keys : axis.value_key ? [axis.value_key] : []).map((key) => ({ key, axis }));
  });
  const keys = key_axes.map(({ key }) => key);
  const grouping_key = typeof props.grouping_key === 'string' ? props.grouping_key : undefined;
  const colors = props.colors as Record<string, string> | undefined;
  const lines = props.lines as LinesProps | undefined;
  const color_key = typeof props.color_key === 'string' ? props.color_key : 'color';
  const checkIfSelected = props.checkIfSelected as ChartCheckHandler | undefined;
  const grouped = Boolean(grouping_key) && row[grouping_key as string] !== undefined;
  const rows = grouped && x_key ? getRowsAtX(data, x_key, row[x_key]) : [row];
  const shown_keys = grouped ? key_axes
    : key_axes.filter(({ key }) => checkIfSelected?.(getKeyItem(key, grouping_key), 'path', props.selected as Record<string, string[]> | undefined, { grouping_key, selected: props.selected as Record<string, string[]> | undefined }) !== false);
  return rows.flatMap((item) => shown_keys.flatMap(({ key, axis: y_axis }) => {
    const value = y_axis.scale_type === 'time' ? toNumberTimestamp(item[key])
      : y_axis.scale_type === 'point' ? item[key] : toNumber(item[key]);
    if (value === undefined || value === null) return [];
    const group = grouped ? String(item[grouping_key as string]) : undefined;
    const name = group !== undefined ? (keys.length > 1 ? `${group} ${key}` : group) : keys.length > 1 ? key : (y_axis.label ?? key);
    const style_name = group ?? key;
    const row_color = item[color_key];
    const style = { ...lines?.[key], ...(group !== undefined ? lines?.[group] : undefined) };
    const color = style.color ?? colors?.[style_name] ?? colors?.[key] ?? (typeof row_color === 'string' ? row_color : undefined) ?? (typeof props.color === 'string' ? props.color : undefined);
    const default_text = y_axis.scale_type === 'time' ? convertToZonedTimestamp(value as number, props.time_zone as string | undefined, 'full')
      : typeof value === 'number' ? COMPACT.format(value) : String(value);
    const text = y_axis.format?.({ value: value as string | number | Date, position: `${y_axis.scale?.(value) ?? 0}%` }, 'tooltip', { ...y_axis, time_zone: props.time_zone }) ?? default_text;
    return [{ id: `${group ?? ''}:${key}`, name, color, text, unit: y_axis.unit }];
  }));
};
